import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg, inr } from "@/lib/api";
import { PageHeader, Card, Field, Select, Btn, MpinField, ReceiptDialog } from "@/components/shared";
import { useMeta, useSubmit } from "@/pages/Services";

export default function Bbps() {
  const meta = useMeta();
  const [f, setF] = useState({ category: "", biller: "", consumer_no: "", customer_mobile: "" });
  const [bill, setBill] = useState(null);
  const [amount, setAmount] = useState("");
  const [mpin, setMpin] = useState("");
  const [fetching, setFetching] = useState(false);
  const { loading, receipt, setReceipt, run } = useSubmit(() => { setBill(null); setMpin(""); setF({ ...f, consumer_no: "" }); });
  const cats = meta ? Object.keys(meta.bbps) : [];

  const fetchBill = async (e) => {
    e.preventDefault();
    setFetching(true);
    try {
      const { data } = await api.post("/bbps/fetch", f);
      setBill(data);
      setAmount(String(data.bill_amount));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setFetching(false);
    }
  };

  const pay = () => run("/bbps/pay", { ...f, amount: Number(amount), customer_name: bill.customer_name, mpin }, "Bill paid successfully");

  return (
    <div>
      <PageHeader title="BBPS Bill Payment" subtitle="Bharat Bill Payment System — electricity, water, gas, FASTag, insurance & more." />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="bbps-categories">
        {cats.map((c) => (
          <button key={c} onClick={() => { setF({ ...f, category: c, biller: "" }); setBill(null); }}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${f.category === c ? "border-blue-700 bg-blue-700 text-white" : "border-slate-200 bg-white hover:border-blue-300"}`}
            data-testid={`bbps-cat-${c.replace(/\W+/g, "-").toLowerCase()}`}>{c}</button>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Biller details">
          <form onSubmit={fetchBill} className="space-y-4" data-testid="bbps-fetch-form">
            <Field label="Category"><Select options={cats} placeholder="Select Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value, biller: "" })} required data-testid="bbps-category-select" /></Field>
            <Field label="Biller / Operator"><Select options={meta?.bbps[f.category] || []} placeholder="Select Biller" value={f.biller} onChange={(e) => setF({ ...f, biller: e.target.value })} required data-testid="bbps-biller-select" /></Field>
            <Field label="Consumer / Account Number"><input className="np-input font-mono" value={f.consumer_no} onChange={(e) => setF({ ...f, consumer_no: e.target.value })} required data-testid="bbps-consumer-no-input" /></Field>
            <Field label="Customer Mobile"><input className="np-input" maxLength={10} value={f.customer_mobile} onChange={(e) => setF({ ...f, customer_mobile: e.target.value.replace(/\D/g, "") })} data-testid="bbps-mobile-input" /></Field>
            <Btn type="submit" loading={fetching} data-testid="fetch-bill-button">Fetch Bill</Btn>
          </form>
        </Card>
        <Card title="Bill summary">
          {!bill ? <div className="py-14 text-center text-sm text-slate-400">Fetch a bill to see details here.</div> : (
            <div className="space-y-4" data-testid="bill-details">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[["Customer", bill.customer_name], ["Bill No.", bill.bill_number], ["Bill Date", bill.bill_date], ["Due Date", bill.due_date], ["Biller", bill.biller], ["Consumer No.", bill.consumer_no]].map(([k, v]) => (
                  <div key={k}><div className="np-label">{k}</div><div className="font-semibold">{v}</div></div>
                ))}
              </div>
              <div className="rounded-lg bg-slate-50 p-3"><div className="np-label">Bill Amount</div><div className="font-mono text-2xl font-bold" data-testid="bill-amount">{inr(bill.bill_amount)}</div></div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Pay Amount"><input type="number" className="np-input font-mono" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="bbps-amount-input" /></Field>
                <MpinField value={mpin} onChange={setMpin} />
              </div>
              <Btn variant="green" onClick={pay} loading={loading} className="w-full" data-testid="pay-bill-button">Pay {inr(amount)}</Btn>
            </div>
          )}
        </Card>
      </div>
      <ReceiptDialog txn={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}
